'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/prob-extra'

const EntropyBars = dynamic(() => import('../_components/widgets/ProbExtraWidgets').then(m => m.EntropyBars), { ssr: false })
const KLEditor = dynamic(() => import('../_components/widgets/ProbExtraWidgets').then(m => m.KLEditor), { ssr: false })

export default function EntropyAndKL() {
  return (
    <Lesson>
      <Step>
        <H>How unpredictable is it?</H>
        <P>
          You met <strong>surprise</strong> in the cross-entropy lesson: an outcome with probability <M>p</M> carries{' '}
          <M>{'-\\log p'}</M> worth of surprise. Rare things are surprising; certain things aren’t surprising at all.
        </P>
        <P>
          A distribution’s <strong>entropy</strong> is its average surprise: how unpredictable it is overall. In base-2 logs
          it’s measured in <strong>bits</strong>, the number of yes/no questions you’d need, on average, to find out the outcome.
        </P>
        <MB>{'H(p) = -\\sum_i p_i \\log_2 p_i'}</MB>
        <Figure n="5.13" wide caption="Drag the bars to load the die. A fair die is as unpredictable as possible (2.585 bits); a die that always lands the same way has 0.">
          <EntropyBars />
        </Figure>
      </Step>

      <Step gate={['nq-bits', 'q-entropy']}>
        <H>Bits</H>
        <NumberQuiz
          id="nq-bits"
          question="A fair 8-sided die. How many bits of entropy does one roll have?"
          answer={3}
          explain="Each outcome has p = 1/8 and surprise log₂ 8 = 3 bits. Three yes/no questions (is it in the top half? …) always find the answer."
          hint="With n equally likely outcomes, the entropy is log₂ n."
        />
        <Quiz
          id="q-entropy"
          question="Which has the highest entropy?"
          options={[
            { label: 'A fair coin', correct: true },
            { label: 'A coin that lands heads 90% of the time', why: 'You can usually guess this one: about 0.47 bits.' },
            { label: 'A two-headed coin', why: 'Completely predictable: 0 bits.' },
          ]}
        />
      </Step>

      <Step gate="entropy-kl">
        <H>The cost of a wrong guess</H>
        <P>
          Suppose the truth is <M>p</M> but you believe <M>q</M>. Your average surprise is then the{' '}
          <strong>cross-entropy</strong> <M>{'H(p, q)'}</M>. It can never be less than <M>{'H(p)'}</M>: you can’t beat the truth
          at predicting itself. The gap between them is the <strong>Kullback–Leibler divergence</strong>, the price of believing
          the wrong thing:
        </P>
        <MB>{'D_{\\text{KL}}(p \\,\\|\\, q) = H(p, q) - H(p) = \\sum_i p_i \\log_2 \\frac{p_i}{q_i}'}</MB>
        <Figure n="5.14" wide caption="Shape the truth p and the model’s guess q. KL is 0 only when they match exactly. Notice KL(p‖q) and KL(q‖p) differ.">
          <KLEditor />
        </Figure>
        <Exercise
          id="entropy-kl"
          title="entropy, crossEntropy, kl"
          {...EX.entropyKL}
          hints={[
            <>
              <C>{'for (const x of p) if (x > 0) total -= x * Math.log2(x)'}</C>
            </>,
            <>
              crossEntropy: the same loop, but <C>{'Math.log2(q[i])'}</C> instead of <C>{'Math.log2(p[i])'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['q-kl', 'char-entropy']}>
        <H>Why this matters for AI</H>
        <P>
          Training a language model minimises cross-entropy <M>{'H(\\text{data}, \\text{model})'}</M>. The entropy of the data
          itself is fixed, so minimising cross-entropy is the same as minimising the KL divergence from the data to the model.
          Training is making the model’s beliefs match reality.
        </P>
        <Quiz
          id="q-kl"
          question="A language model’s loss stops falling at 1.2 bits per character, and never reaches 0. The most likely reason is…"
          options={[
            { label: 'Text has real entropy of its own: even a perfect model can’t predict every next letter', correct: true },
            { label: 'The model is broken', why: 'Not necessarily. Some unpredictability is in the text itself.' },
            { label: 'Cross-entropy can’t go below 1', why: 'It can reach 0 for perfectly predictable data. Real text isn’t.' },
          ]}
          explain="The loss can only fall to the entropy of the data. Everything above that is KL: room for the model to improve."
        />
        <Exercise
          id="char-entropy"
          title="How many bits is a letter worth?"
          {...EX.charEntropy}
          hints={[<>Count each character in a Map. Then for each count: <C>{'const p = k / text.length'}</C> and <C>{'h -= p * Math.log2(p)'}</C>.</>]}
        />
        <P>KL divergence turns up all over modern AI:</P>
        <UL>
          <li>
            <strong>Fine-tuning chat models</strong> adds a KL penalty that keeps the tuned model from drifting too far from the
            original.
          </li>
          <li>
            <strong>Distillation</strong> trains a small model to minimise KL to a big model’s predictions.
          </li>
          <li>
            <strong>Diffusion models and VAEs</strong>, the image generators, are trained with KL terms inside their losses.
          </li>
        </UL>
        <Callout kind="note">
          Your letter-frequency number is the loss of a model that ignores context entirely. Models that look at earlier
          letters do much better. In Module 8 you’ll build them and watch the bits per character fall.
        </Callout>
      </Step>
    </Lesson>
  )
}
